import { Popover, PopoverContent, PopoverTrigger, useDisclosure } from '@nextui-org/react'
import React from 'react'
import { cm } from '~/utils/utils'
import { NewGearIcon as GearIcon } from '../icons'
import { Tag } from '../common/Tag'

export interface ConnectionHeaderProps {
  /** Icon to display next to the title */
  icon?: React.ReactNode
  /** Main title text */
  title: string
  /** Whether to show the connected status badge */
  showConnectedBadge?: boolean
  /** Text for the connected badge */
  connectedBadgeText?: string
  /** Whether to show the back button */
  showBackButton?: boolean
  /** Whether to show the settings button */
  showSettingsButton?: boolean
  /** Settings button text */
  settingsButtonText?: string
  /** Custom icon for the settings button */
  settingsButtonIcon?: React.ReactNode
  /** Callback when back button is clicked */
  onBackClick?: () => void
  /** Callback when settings button is clicked */
  onSettingsClick?: () => void
  /** Content to show in the settings popover */
  settingsPopoverContent?: React.ReactNode
  /** Custom className for the container */
  className?: string
  /** Custom style for the container */
  style?: React.CSSProperties
  /** Whether the header is for a local connection */
  isLocal?: boolean
}

export const Header: React.FC<ConnectionHeaderProps> = ({
  icon,
  title,
  showConnectedBadge = false,
  connectedBadgeText = 'CONNECTED',
  showBackButton = true,
  showSettingsButton = true,
  settingsButtonText = 'Manage Connection',
  settingsButtonIcon,
  onBackClick,
  onSettingsClick,
  settingsPopoverContent,
  className,
  style,
  isLocal = false
}) => {
  const { isOpen: settingsPopoverOpen, onOpenChange: onSettingsPopoverOpenChange } = useDisclosure()

  return (
    <div
      className={cm(
        'h-[68px] rounded-[16px] w-full bg-[rgba(0,0,0,0.04)] flex items-center justify-between p-4',
        className
      )}
      style={style}
    >
      {/* Left side - Back button and title */}
      <div className='flex items-center'>
        {showBackButton && (
          <button onClick={onBackClick} className='rounded-lg flex items-center justify-center'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
            >
              <path
                d='M7.825 13L13.425 18.6L12 20L4 12L12 4L13.425 5.4L7.825 11H20V13H7.825Z'
                fill='#1D1B20'
              />
            </svg>
          </button>
        )}

        <div
          className={`flex items-center ${showBackButton ? 'ml-3' : ''} flex-1 justify-center ${isLocal ? 'gap-x-[9px]' : 'gap-x-2'}`}
        >
          {icon && <div className='w-5 h-5 flex items-center justify-center'>{icon}</div>}
          <span className='text-[15px] font-semibold text-[rgba(46,31,54,0.90)] font-mono'>
            {title}
          </span>
          {showConnectedBadge && !isLocal && (
            <Tag variant='success' className='w-[71px] h-4'>
              {connectedBadgeText}
            </Tag>
          )}
        </div>
      </div>

      {/* Right side - Settings button (only when not local and when showSettingsButton is true) */}
      {showSettingsButton && !isLocal && (
        <>
          {settingsPopoverContent ? (
            <Popover
              isOpen={settingsPopoverOpen}
              onOpenChange={onSettingsPopoverOpenChange}
              placement='bottom-start'
              offset={5}
              onClose={() => {
                onSettingsPopoverOpenChange()
              }}
              classNames={{
                content: 'shadow-popover bg-white w-[235px]',
                trigger: 'aria-expanded:!opacity-100 aria-expanded:!scale-100'
              }}
            >
              <PopoverTrigger>
                <button
                  className={cm(
                    'h-9 w-[189px] flex p-[8px_16px] justify-center items-center gap-2 text-base rounded-[12px] border border-tokens-neutral-border-1 bg-transparent text-tokens-neutral-text-primary transition-colors hover:bg-tokens-neutral-fill-fill-6 active:bg-tokens-neutral-fill-fill-4',
                    settingsPopoverOpen ? 'bg-tokens-neutral-fill-fill-6 !opacity-100' : ''
                  )}
                  onClick={onSettingsClick}
                >
                  {settingsButtonIcon || <GearIcon />}
                  <span className='font-normal'>{settingsButtonText}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className='!rounded-xl p-2'>{settingsPopoverContent}</PopoverContent>
            </Popover>
          ) : (
            <button
              className='h-9 w-[189px] flex p-[8px_16px] justify-center items-center gap-2 text-base rounded-[12px] border border-gray-300 bg-black text-white transition-colors hover:bg-gray-800 active:bg-gray-700'
              onClick={onSettingsClick}
            >
              {settingsButtonIcon || <GearIcon />}
              <span className='font-normal'>{settingsButtonText}</span>
            </button>
          )}
        </>
      )}
    </div>
  )
}
